import SERVICE_REGISTRY from "../services/registry";
import { SETTINGS } from "../settings";
import { DataSource } from "../stores-js/datasource";
import { buildQueryString } from "../stores-js/network";
import { SearchParameters } from "../stores-js/searchparameters";

export function debouncePromise(fn, delay) {
    let timeoutId = null;
    let latestResolve = null;
    let latestReject = null;

    return (...args) =>
        new Promise((resolve, reject) => {
            if (timeoutId !== null) {
                clearTimeout(timeoutId);
            }

            latestResolve = resolve;
            latestReject = reject;
            timeoutId = setTimeout(() => {
                fn(...args)
                    .then((result) => {
                        if (latestResolve === resolve && latestReject === reject) {
                            resolve(result);
                        }
                    })
                    .catch((error) => {
                        if (latestResolve === resolve && latestReject === reject) {
                            reject(error);
                        }
                    });
            }, delay);
        });
}

class Data {
    constructor() {
        this.geocoder = new google.maps.Geocoder();
        this.dataSource = new DataSource();
        this.streeViewService = null; //new google.maps.StreetViewService();
        this.localitiesService = new woosmap.map.LocalitiesService();
        this.allStores = null;
        this.WOOSMAP_KEY = Bun.env.WOOSMAP_KEY;
        this.debouncedLocalitiesAutocomplete = debouncePromise(
            (request) => this.localitiesService.autocomplete(request),
            100,
        );
    }

    geocode(address, callback) {
        const request = { address };
        // eslint-disable-next-line no-unused-vars
        this.geocoder.geocode(request, (results, status, errorMessage) => {
            if (status === google.maps.GeocoderStatus.OK) {
                callback(
                    results[0].geometry.viewport,
                    {
                        lat: results[0].geometry.location.lat(),
                        lng: results[0].geometry.location.lng(),
                    },
                    results[0].formatted_address,
                );
            } else if (
                status === google.maps.GeocoderStatus.ZERO_RESULTS ||
                status === google.maps.GeocoderStatus.ERROR ||
                status === google.maps.GeocoderStatus.INVALID_REQUEST ||
                status === google.maps.GeocoderStatus.REQUEST_DENIED
            ) {
                console.error("Geocoding error");
                callback();
            } else if (
                status === google.maps.GeocoderStatus.OVER_QUERY_LIMIT ||
                status === google.maps.GeocoderStatus.UNKNOWN_ERROR
            ) {
                window.setTimeout(() => {
                    this.geocode(address, callback);
                }, 1000);
            }
        });
    }

    reverseGeocode(location, callback) {
        const request = { location };
        // eslint-disable-next-line no-unused-vars
        this.geocoder.geocode(request, (results, status, errorMessage) => {
            if (status === google.maps.GeocoderStatus.OK) {
                callback(results);
            } else if (
                status === google.maps.GeocoderStatus.ZERO_RESULTS ||
                status === google.maps.GeocoderStatus.ERROR ||
                status === google.maps.GeocoderStatus.INVALID_REQUEST ||
                status === google.maps.GeocoderStatus.REQUEST_DENIED
            ) {
                console.error("Geocoding error");
                callback();
            } else if (
                status === google.maps.GeocoderStatus.OVER_QUERY_LIMIT ||
                status === google.maps.GeocoderStatus.UNKNOWN_ERROR
            ) {
                window.setTimeout(() => {
                    this.geocode(location, callback);
                }, 1000);
            }
        });
    }

    getStreetViewPanoramaByLocation(location, radius, successCallback, errorCallback) {
        this.streeViewService.getPanoramaByLocation(location, radius, (data, status) => {
            if (status === google.maps.StreetViewStatus.OK) {
                successCallback(data);
            } else if (radius < 10000000) {
                this.getStreetViewPanoramaByLocation(location, radius * 2, successCallback, errorCallback);
            } else {
                errorCallback();
            }
        });
    }

    getAllStores(callback) {
        if (this.allStores) {
            callback(this.allStores);
        } else {
            this.dataSource.getAllStores((data) => {
                this.allStores = data;
                callback(this.allStores);
            });
        }
    }

    getNearbyStores(latitude, longitude, callback) {
        return this.dataSource.getNearbyStores(latitude, longitude, callback);
    }

    getStoreById(id, callback) {
        const searchParameters = new SearchParameters({
            query: `idstore:=${id}`,
        });
        return this.dataSource.searchStoresByParameters(searchParameters, callback);
    }

    getBoundsForCountry(country, callback) {
        const query = `country:="${country}"`;

        const url = `https://api.woosmap.com/localities/geocode?address=${encodeURIComponent(
            country,
        )}&fields=geometry&types=country&language=fr&key=${this.WOOSMAP_KEY}`;

        // The geocode can transiently fail or return no results (e.g. flaky
        // network, locale mismatch). Retry once, then give up gracefully: the
        // callback is only invoked with a valid viewport, so on total failure
        // the map keeps its initial view instead of throwing on results[0].
        const attempt = (retriesLeft) =>
            fetch(url)
                .then((result) => result.json())
                .then((position) => {
                    const viewport = position?.results?.[0]?.geometry?.viewport;
                    if (viewport?.northeast && viewport?.southwest) {
                        callback(viewport);
                    } else if (retriesLeft > 0) {
                        attempt(retriesLeft - 1);
                    } else {
                        console.warn(`getBoundsForCountry: no results for "${country}"`);
                    }
                })
                .catch((error) => {
                    if (retriesLeft > 0) {
                        attempt(retriesLeft - 1);
                    } else {
                        console.warn(`getBoundsForCountry: geocode failed for "${country}"`, error);
                    }
                });

        attempt(1);
        // woosmap.$.ajax({
        //   url: `https://api.woosmap.com/stores/search/bounds?key=${this.WOOSMAP_KEY}&query=${query}`,
        //   success: (response) => {console.log(response); callback(response.bounds)},
        //   error() {
        //   },
        // });
    }

    woosGeocode(address, callback) {
        const request = { address };
        this.localitiesService.geocode(request).then((localities) =>
            callback(
                localities.results[0].geometry.viewport,
                localities.results[0].geometry.location,
                localities.results[0].formatted_address,
            ).catch((error) => {
                console.error("Error geocoding localities:", error);
                callback();
            }),
        );
    }

    autocomplete(input, callback) {
        // Aviation portal: restrict suggestions to localities only.
        const types = SETTINGS.isAviationPortal
            ? ["locality"]
            : [
                  "locality",
                  "address",
                  "postal_code",
                  "transit.station",
                  "tourism",
                  "government",
                  "business.mall",
                  "business.shop.mall",
                  "education",
                  "park",
                  "place_of_worship",
                  "business.parking",
                  "sports",
                  "business.theatre",
              ];
        const request = {
            input,
            types,
            components: {
                country: [SETTINGS.country],
            },
        };
        this.debouncedLocalitiesAutocomplete(request)
            .then((localities) => callback(localities.localities))
            .catch((error) => console.error("Error autocomplete localities:", error));
    }

    woosAutocompleteDetails(publicId, callback) {
        this.localitiesService
            .getDetails({ publicId })
            .then((locality) => callback(locality.result))
            .catch((error) => console.error("Error getting locality details:", error));
    }

    updateStoresWithWoosmapDistanceApi(stores, callback) {
        const originLocation = SERVICE_REGISTRY.locationService.getLocation();
        const distanceOptions = {
            distanceapiUrl: "https://api.woosmap.com/distance/distancematrix/json",
            units: SETTINGS.unitSystem,
            mode: woosmap.map.TravelMode.DRIVING,
            language: SETTINGS.language,
            elements: "duration_distance",
            key: this.WOOSMAP_KEY,
        };
        if (originLocation && stores && stores.length > 0) {
            const destinations = stores.map(
                (store) => `${store.geometry.coordinates[1]},${store.geometry.coordinates[0]}`,
            );

            const parameters = buildQueryString({
                origins: `${originLocation.lat},${originLocation.lng}`,
                destinations: destinations.join("|"),
                units: distanceOptions.units,
                mode: distanceOptions.mode,
                language: distanceOptions.language,
                elements: distanceOptions.elements,
                avoidHighways: SERVICE_REGISTRY.itinaryService.getOptions().avoidHighways,
                avoidTolls: SERVICE_REGISTRY.itinaryService.getOptions().avoidTolls,
                avoidFerries: SERVICE_REGISTRY.itinaryService.getOptions().avoidFerries,
                key: distanceOptions.key,
            });

            const url = `${distanceOptions.distanceapiUrl}?${parameters}`;

            fetch(url)
                .then((response) => response.json())
                .then((response) => {
                    if (response.status === "OK") {
                        const distanceObj = response.rows[0].elements;
                        for (let i = 0; i < stores.length; i++) {
                            const element = distanceObj[i];
                            // Only OK elements carry distance/duration; any error status
                            // (NOT_FOUND, ZERO_RESULTS, MAX_ROUTE_LENGTH_EXCEEDED, …) omits them.
                            if (element && element.status === "OK" && element.distance && element.duration) {
                                stores[i].properties.distance = element.distance.value;
                                stores[i].properties.duration = element.duration.value;
                                stores[i].properties.durationText = element.duration.text;
                            } else {
                                // Push unreachable POIs to the end of the ranked list.
                                stores[i].properties.duration = Number.POSITIVE_INFINITY;
                            }
                        }
                        stores.sort((a, b) => {
                            if (a.properties.duration === b.properties.duration) return 0;
                            if (a.properties.duration > b.properties.duration) return 1;
                            return -1;
                        });
                    }
                    callback(stores);
                })
                .catch((error) => {
                    console.error("Distance Matrix error:", error);
                    callback(stores);
                });
        } else {
            callback(stores);
        }
    }
}
const DATA = new Data();

export default DATA;
